import React, { Component, Fragment } from "react";
import { Switch, Route, withRouter, Redirect } from "react-router-dom";
import ViewsContainer from "../../components/views/ViewsContainer";
import LoadingWizard from "../../components/spinner/wizard";
import RegistrantDetail from "./registrantDetail";
import AddRegistrantModal from "./modals/addRegistrantModal";
import DeleteRegistrantsModal from "./modals/deleteRegistrantsModal";
import moment from "moment";
import API from "../../utils/API";

class BookingRegistrants extends Component {
	constructor(props) {
		super(props);

		// Define base columns for registrants
		let customFieldColumns = {
			emailAddress: "Email",
			firstName: "First Name",
			lastName: "Last Name",
			phoneNumber: "Phone Number",
			company: "Company",
			totalBookings: "Total Bookings",
			approvedBookings: "Approved Bookings",
			pendingBookings: "Pending Bookings",
			lastBookingDate: "Last Booking",
			createdAt: "Date Added",
			updatedAt: "Last Updated",
			notes: "Notes",
		};

		let fieldCategories = {
			basics: {
				title: "General Information",
				columns: [
					"emailAddress",
					"firstName",
					"lastName",
					"phoneNumber",
					"company",
					"notes",
				],
			},
			bookingInfo: {
				title: "Booking Information",
				columns: [
					"totalBookings",
					"approvedBookings",
					"pendingBookings",
					"lastBookingDate",
				],
			},
			timestamps: {
				title: "Dates",
				columns: ["createdAt", "updatedAt"],
			},
		};

		let defaultFieldTypes = {
			emailAddress: "email",
			firstName: "text",
			lastName: "text",
			phoneNumber: "text",
			company: "text",
			totalBookings: "number",
			approvedBookings: "number",
			pendingBookings: "number",
			lastBookingDate: "timestamp",
			createdAt: "timestamp",
			updatedAt: "timestamp",
			notes: "text",
		};

		const registrants = this.processRegistrantsData(
			props.bookingPage?.registrants || []
		);

		this.state = {
			bookingPageId: props.bookingPage._id,
			allRegistrants: registrants,
			sortedRegistrants: registrants,
			categoryColumns: fieldCategories,
			columns: customFieldColumns,
			columnTypes: defaultFieldTypes,
			checkedRegistrants: {},
			allChecked: false,
			registrantSelected: false,
			selectedRegistrant: 0,
			addRegistrantModalOpen: false,
			deleteRegistrantsModalOpen: false,
			newRegistrant: { firstName: "", lastName: "", emailAddress: "" },
			loading: false,
			sidePanelOpen: false,
			activeRow: "",
			editCount: 0,
		};
	}

	componentDidMount() {
		// In a real implementation, this would fetch registrants from the API
		this.setState({ loading: false });
	}

	componentDidUpdate(prevProps) {
		// Reprocess registrants when booking page data changes
		if (
			prevProps.bookingPage?.registrants !== this.props.bookingPage?.registrants
		) {
			const processedRegistrants = this.processRegistrantsData(
				this.props.bookingPage?.registrants || []
			);
			this.setState({
				allRegistrants: processedRegistrants,
				sortedRegistrants: processedRegistrants,
				editCount: this.state.editCount + 1,
			});
		}
	}

	processRegistrantsData = (registrants) => {
		return registrants.map((registrant) =>
			this.processRegistrantData(registrant)
		);
	};

	processRegistrantData = (registrant) => {
		const { bookingPage, orgId } = this.props;

		// Get email from the appropriate nested object (user or attendee)
		const registrantEmail = bookingPage.eventId 
			? registrant.attendee?.email 
			: registrant.user?.emailAddress;

		const registrantRequests =
			bookingPage.requests?.filter(
				(request) => request.submission?.formData?.email === registrantEmail
			) || [];

		let totalBookings = 0;
		let approvedBookings = 0;
		let pendingBookings = 0;
		let lastBookingDate = null;

		registrantRequests.forEach((request) => {
			// Count total requested slots
			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach((date) => {
					Object.keys(request.selectedDateTimes[date]).forEach((calendarId) => {
						totalBookings += request.selectedDateTimes[date][calendarId].length;
					});
				});
			}

			// Count approved slots
			if (request.approvedTimes) {
				Object.keys(request.approvedTimes).forEach((date) => {
					Object.keys(request.approvedTimes[date]).forEach((calendarId) => {
						approvedBookings += request.approvedTimes[date][calendarId].length;
					});
				});
			}

			// Count pending slots (requested but not yet approved/denied)
			const requestedSlots = totalBookings;
			const reviewedSlots =
				approvedBookings +
				(request.deniedTimes
					? Object.keys(request.deniedTimes).reduce((total, date) => {
						return (
							total +
							Object.keys(request.deniedTimes[date]).reduce(
								(dateTotal, calendarId) => {
									return (
										dateTotal + request.deniedTimes[date][calendarId].length
									);
								},
								0
							)
						);
					}, 0)
					: 0);
			pendingBookings += Math.max(0, requestedSlots - reviewedSlots);

			// Track last booking date
			const requestDate = new Date(request.submittedAt || request.createdAt);
			if (!lastBookingDate || requestDate > lastBookingDate) {
				lastBookingDate = requestDate;
			}
		});

		// Flatten the nested user/attendee data for display
		const userData = bookingPage.eventId && registrant.attendee ? registrant.attendee : registrant.user;
		return {
			...registrant,
			id: registrant._id,
			link: `/${orgId}/booking/${bookingPage._id}/registrants/${registrant._id}`,
			// Flatten user/attendee data for table display
			firstName: userData?.firstName || '',
			lastName: userData?.lastName || '',
			emailAddress: userData?.email || userData?.emailAddress || '',
			phoneNumber: userData?.phoneNumber || '',
			company: userData?.company || '',
			title: userData?.title || '',
			description: userData?.description || '',
			totalBookings,
			approvedBookings,
			pendingBookings,
			lastBookingDate: lastBookingDate ? lastBookingDate.toISOString() : null,
		};
	};

	toggleAddRegistrant = () => {
		this.setState({
			newRegistrant: { firstName: "", lastName: "", emailAddress: "" },
			addRegistrantModalOpen: !this.state.addRegistrantModalOpen,
		});
	};

	addRegistrant = async (registrant) => {
		try {
			const res = await API().patch(
				`Organizations/${this.props.orgId}/bookingPages/${this.state.bookingPageId}`,
				{
					request: "addRegistrant",
					registrant: registrant,
				}
			);

			if (!res.data?.registrant) throw new Error("Failed to add registrant");
			const newRegistrant = this.processRegistrantData(res.data.registrant);

			const updatedRegistrants = [
				...this.state.sortedRegistrants,
				newRegistrant,
			];

			this.setState({
				sortedRegistrants: updatedRegistrants,
				allRegistrants: updatedRegistrants,
				editCount: this.state.editCount + 1,
			});

			// Call parent method to update booking page data
			this.props.addRegistrant && this.props.addRegistrant(newRegistrant);
		} catch (error) {
			console.error("Error adding registrant:", error);
			
			// Extract error message from API response
			let errorMessage = "Failed to add registrant. Please try again.";
			
			if (error.response?.status === 400) {
				// Handle specific 400 errors
				if (error.response.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error.response.data?.error) {
					errorMessage = error.response.data.error;
				} else {
					errorMessage = "A registrant with this email already exists.";
				}
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			}
			
			// Re-throw the error with the proper message so the modal can handle it
			throw new Error(errorMessage);
		}
	};

	toggleDeleteRegistrants = (checkedRegistrants) => {
		this.setState({
			checkedRegistrants: checkedRegistrants || {},
			deleteRegistrantsModalOpen: !this.state.deleteRegistrantsModalOpen,
		});
	};

	deleteCheckedRegistrants = async () => {
		const registrantIds = Object.keys(this.state.checkedRegistrants);
		
		const res = await API().patch(
			`Organizations/${this.props.orgId}/bookingPages/${this.state.bookingPageId}`,
			{
				request: "removeRegistrants",
				registrantIds: registrantIds,
			}
		);

		if (!res.data) throw new Error("Failed to delete registrants");

		const updatedRegistrants = this.state.sortedRegistrants.filter(
			(r) => !registrantIds.includes(r._id)
		);

		this.setState({
			checkedRegistrants: {},
			sortedRegistrants: updatedRegistrants,
			allRegistrants: updatedRegistrants,
			editCount: this.state.editCount + 1,
		});

		// Call parent method to update booking page data
		this.props.deleteRegistrants && this.props.deleteRegistrants(registrantIds);
		this.toggleDeleteRegistrants();
	};
	deleteSingleRegistrant = async () => {
		const registrantIds = Object.keys(this.state.checkedRegistrants);
		
		const res = await API().patch(
			`Organizations/${this.props.orgId}/bookingPages/${this.state.bookingPageId}`,
			{
				request: "removeRegistrant",
				registrantIds: registrantIds,
			}
		);

		if (!res.data) throw new Error("Failed to delete registrants");

		const updatedRegistrants = this.state.sortedRegistrants.filter(
			(r) => !registrantIds.includes(r._id)
		);

		this.setState({
			checkedRegistrants: {},
			sortedRegistrants: updatedRegistrants,
			allRegistrants: updatedRegistrants,
			editCount: this.state.editCount + 1,
		});

		// Call parent method to update booking page data
		this.props.deleteRegistrants && this.props.deleteRegistrants(registrantIds);
		this.toggleDeleteRegistrants();
	};

	deleteSingleRegistrant = async (registrantId) => {
		const res = await API().patch(
			`Organizations/${this.props.orgId}/bookingPages/${this.state.bookingPageId}`,
			{
				request: "removeRegistrant",
				registrantIds: [registrantId], // Convert single ID to array
			}
		);

		if (!res.data) throw new Error("Failed to delete registrant");

		const updatedRegistrants = this.state.sortedRegistrants.filter(
			(r) => r._id !== registrantId
		);

		this.setState({
			sortedRegistrants: updatedRegistrants,
			allRegistrants: updatedRegistrants,
			editCount: this.state.editCount + 1,
		});

		// Call parent method to update booking page data
		this.props.deleteRegistrants && this.props.deleteRegistrants([registrantId]);
	};

	updateRegistrant = async (registrantId, updatedData) => {
		const res = await API().patch(
			`Organizations/${this.props.orgId}/bookingPages/${this.state.bookingPageId}`,
			{
				request: "updateRegistrant",
				registrant: {
					_id: registrantId,
					...updatedData
				}
			}
		);
		const updatedRegistrants = this.state.sortedRegistrants.map((r) => {
			if (r._id === registrantId) {
				return { ...r, ...updatedData, updatedAt: new Date().toISOString() };
			}
			return r;
		});

		this.setState({
			sortedRegistrants: updatedRegistrants,
			allRegistrants: updatedRegistrants,
			editCount: this.state.editCount + 1,
		});

		// Call parent method to update booking page data
		this.props.updateRegistrant &&
			this.props.updateRegistrant(registrantId, updatedData);
	};

	setActiveRow = (registrantId) => {
		this.setState({ activeRow: registrantId });
	};

	render() {
		const {
			categoryColumns,
			columns,
			columnTypes,
			sortedRegistrants,
			addRegistrantModalOpen,
			deleteRegistrantsModalOpen,
			loading,
			sidePanelOpen,
			activeRow,
			editCount,
		} = this.state;

		const { bookingPage, orgId } = this.props;
		return (
			<Switch>
				<Route path={`/:organization/booking/:bookingId/registrants`}>
					<Fragment>
						{!loading && (
							<ViewsContainer
								activeRow={activeRow}
								sidePanelOpen={sidePanelOpen}
								orgId={orgId}
								key={`registrants${editCount}`}
								views={[]} // No custom views for now
								entries={sortedRegistrants}
								categoryColumns={categoryColumns}
								columns={columns}
								columnTypes={columnTypes}
								title="Registrants"
								description="Manage people who have submitted booking requests. Track their booking history and prevent scheduling conflicts."
								readOnly={false}
								mainActions={[
									{
										type: "button",
										onClick: this.toggleAddRegistrant,
										label: "Add Registrant",
									},
								]}
								batchActions={[
									{
										label: "Delete",
										iconClass: "las la-trash-alt mr-5",
										class: "danger",
										onClick: this.toggleDeleteRegistrants,
									},
								]}
								breadcrumbs={[{ name: bookingPage.name || "Booking Page" }]}
								enableDownloadViews={true}
								type={"Registrant"}
								referenceCollection={"BookingPages"}
								referenceId={bookingPage._id}
								section={"registrants"}
								columnWidths={{
									emailAddress: 250,
									firstName: 150,
									lastName: 150,
									phoneNumber: 150,
									company: 200,
									totalBookings: 130,
									approvedBookings: 150,
									pendingBookings: 140,
									lastBookingDate: 180,
								}}
								emptyStateTitle="No Registrants Yet"
								emptyStateDescription="When people submit booking requests, they will appear here as registrants for you to manage."
								emptyStateIcon="las la-user-plus"
								emptyStateAction={{
									label: "Add First Registrant",
									onClick: this.toggleAddRegistrant,
								}}
							/>
						)}

						<LoadingWizard loading={loading} text="Loading Registrants" />

						{addRegistrantModalOpen && (
							<AddRegistrantModal
								isOpen={addRegistrantModalOpen}
								handleSubmit={this.addRegistrant}
								toggle={this.toggleAddRegistrant}
								bookingPageId={bookingPage._id}
								orgId={orgId}
							/>
						)}

						{deleteRegistrantsModalOpen && (
							<DeleteRegistrantsModal
								isOpen={deleteRegistrantsModalOpen}
								delete={this.deleteCheckedRegistrants}
								toggle={this.toggleDeleteRegistrants}
								count={Object.keys(this.state.checkedRegistrants).length}
								registrantIds={Object.keys(this.state.checkedRegistrants)}
								bookingPageId={bookingPage._id}
								orgId={orgId}
							/>
						)}

						{/* Side Panel */}
						<div
							className={`sidePanelContainer ${sidePanelOpen ? "panelOpen" : ""
								}`}
						>
							<Switch>
								<Route
									path={`/:organization/booking/:bookingId/registrants/:registrantId`}
									render={(props) => {
										const registrantId = props.match.params.registrantId;
										const registrant = sortedRegistrants.find(
											(r) => r._id === registrantId
										);
										if (!registrant)
											return (
												<Redirect
													to={`/${orgId}/booking/${bookingPage._id}/registrants`}
												/>
											);
										if (!sidePanelOpen) this.setState({ sidePanelOpen: true });

										return (
											<div className="h-100" key={registrantId}>
												{!loading && (
													<RegistrantDetail
														registrant={registrant}
														bookingPage={bookingPage}
														orgId={orgId}
														updateRegistrant={this.updateRegistrant}
														setActiveRow={this.setActiveRow}
														deleteSingleRegistrant={this.deleteSingleRegistrant}
													/>
												)}
												<LoadingWizard loading={loading} />
											</div>
										);
									}}
								/>
								<Route
									exact
									path={`/:organization/booking/:bookingId/registrants`}
									render={() => {
										if (sidePanelOpen) this.setState({ sidePanelOpen: false });
										return <></>;
									}}
								/>
							</Switch>
						</div>
					</Fragment>
				</Route>
			</Switch>
		);
	}
}

export default withRouter(BookingRegistrants);
